import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fcrEnqueue,
  fcrStatus,
  fcrTrigger,
  fcrStop,
  fcrClearQueue,
  fcrGetProxy,
  fcrSetProxy,
  fcrResetProxy,
  type EnqueueResult,
  type ProxyConfig,
} from "../api/fcr";

// ---------------------------------------------------------------------------
// Number parsing — mirrors backend _norm10 exactly
// ---------------------------------------------------------------------------

// Strip extension suffixes before digit extraction
const EXT_RE = /\s*(x|ext\.?|extension)\s*\d{1,6}\s*$|#\s*\d{1,6}\s*$/i;
// NANP: area code [2-9]XX, exchange [2-9]XX, line XXXX
const NANP_RE = /^[2-9]\d{2}[2-9]\d{6}$/;

function normalizeSingle(raw: string): string | null {
  const stripped = raw.replace(EXT_RE, "").trim();
  let digits = stripped.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length !== 10) return null;
  if (!NANP_RE.test(digits)) return null;
  return digits;
}

interface ParseResult {
  valid: string[];     // unique, normalized 10-digit NANP
  invalidCount: number;
  dupeCount: number;
}

function parseNumbers(raw: string): ParseResult {
  // Split on newlines, commas, or semicolons only — NOT spaces, because a
  // formatted number like "(619) 555-1234" carries its own internal spaces.
  const candidates = raw.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
  const seen = new Set<string>();
  const valid: string[] = [];
  let invalidCount = 0;
  let dupeCount = 0;

  for (const c of candidates) {
    const n = normalizeSingle(c);
    if (!n) {
      // Only count as invalid if the candidate had enough digits to look like
      // a phone number (≥7 digits) — skip blank tokens silently
      if (c.replace(/\D/g, "").length >= 7) invalidCount++;
    } else if (seen.has(n)) {
      dupeCount++;
    } else {
      seen.add(n);
      valid.push(n);
    }
  }

  return { valid, invalidCount, dupeCount };
}

// ---------------------------------------------------------------------------
// Tiny helpers
// ---------------------------------------------------------------------------

function fmt(n: number) {
  return n.toLocaleString();
}

// Surface the backend's own message. On a non-2xx axios rejects before the
// response envelope is unwrapped, so the HTTPException `detail` lives on
// error.response.data — not on error.message (which is just the status line).
function readApiError(e: unknown): string {
  const ax = e as {
    response?: { data?: { detail?: string; error?: string } };
    message?: string;
  };
  return (
    ax.response?.data?.detail ||
    ax.response?.data?.error ||
    ax.message ||
    "Something went wrong"
  );
}

function StatusBadge({ running }: { running: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
        running ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          running ? "bg-green-500 animate-pulse" : "bg-gray-400"
        }`}
      />
      {running ? "Running" : "Idle"}
    </span>
  );
}

function StatTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-gray-50 rounded p-3">
      <div className="text-xs text-gray-500 mb-0.5">{label}</div>
      <div className="text-2xl font-bold font-mono text-gray-800 tabular-nums">
        {fmt(value)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Result banner shown after enqueue
// ---------------------------------------------------------------------------

function EnqueueBanner({ result }: { result: EnqueueResult }) {
  const items: { label: string; value: number; style: string }[] = [
    {
      label: "added to queue",
      value: result.accepted,
      style: result.accepted > 0 ? "text-green-700" : "text-gray-500",
    },
    {
      label: "already registered",
      value: result.already_registered,
      style: "text-gray-500",
    },
    {
      label: "already in queue",
      value: result.already_queued,
      style: "text-gray-500",
    },
  ].filter((i) => i.value > 0);

  return (
    <div className="bg-green-50 border border-green-200 rounded px-3 py-2 text-sm space-y-0.5">
      {items.map((i) => (
        <div key={i.label} className={i.style}>
          <span className="font-mono font-semibold">{fmt(i.value)}</span>{" "}
          {i.label}
        </div>
      ))}
      <div className="text-xs text-gray-500 pt-1">
        {fmt(result.outstanding)} numbers outstanding in queue
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Proxy configuration card
// ---------------------------------------------------------------------------

function ProxyCard() {
  const qc = useQueryClient();
  const { data: proxy, isLoading } = useQuery({
    queryKey: ["fcr-proxy"],
    queryFn: fcrGetProxy,
  });

  // Edit state — null means not editing
  const [editing, setEditing] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function startEdit(p: ProxyConfig) {
    setEnabled(p.enabled);
    setHost(p.host);
    setPort(p.port > 0 ? String(p.port) : "");
    setUsername(p.username);
    setPassword(""); // never pre-fill saved password
    setShowPw(false);
    setError(null);
    setEditing(true);
  }

  const save = useMutation({
    mutationFn: () => {
      const portNum = parseInt(port, 10);
      if (enabled) {
        if (!host.trim()) throw new Error("Host is required");
        if (!port || isNaN(portNum) || portNum < 1 || portNum > 65535)
          throw new Error("Port must be 1–65535");
      }
      return fcrSetProxy({
        enabled,
        host: host.trim(),
        port: portNum || 0,
        username: username.trim(),
        password,
      });
    },
    onSuccess: () => {
      setEditing(false);
      setError(null);
      qc.invalidateQueries({ queryKey: ["fcr-proxy"] });
    },
    onError: (e) => setError(readApiError(e)),
  });

  const reset = useMutation({
    mutationFn: fcrResetProxy,
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["fcr-proxy"] });
    },
    onError: (e) => setError(readApiError(e)),
  });

  if (isLoading || !proxy) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <p className="text-xs text-gray-400">Loading proxy config…</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-800">Proxy</h2>
        {!editing && (
          <button
            onClick={() => startEdit(proxy)}
            className="text-xs text-brand hover:underline"
          >
            Edit
          </button>
        )}
      </div>

      {!editing ? (
        /* ── Read view ─────────────────────────────────────────────── */
        <div className="space-y-1 text-sm">
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${
                proxy.enabled ? "bg-green-500" : "bg-gray-300"
              }`}
            />
            <span className="text-gray-700">
              {proxy.enabled ? "Enabled" : "Disabled (direct)"}
            </span>
            {proxy.source === "script-default" && (
              <span className="text-xs text-gray-400 ml-1">(script default)</span>
            )}
          </div>
          {proxy.enabled && (
            <>
              <div className="font-mono text-xs text-gray-600 bg-gray-50 rounded px-2 py-1 mt-1 break-all">
                {proxy.raw_masked}
              </div>
              {proxy.source === "script-default" && (
                <p className="text-xs text-amber-600 mt-1">
                  Using the script's built-in proxy. Save a custom config above to override.
                </p>
              )}
            </>
          )}
        </div>
      ) : (
        /* ── Edit form ──────────────────────────────────────────────── */
        <div className="space-y-3">
          {/* Enable toggle */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-4 h-4 accent-brand"
            />
            <span className="text-sm text-gray-700">Use proxy</span>
          </label>

          {enabled && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Host</label>
                  <input
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm
                               font-mono focus:outline-none focus:border-brand"
                    placeholder="pg.proxi.es"
                    value={host}
                    onChange={(e) => setHost(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Port</label>
                  <input
                    className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm
                               font-mono focus:outline-none focus:border-brand"
                    placeholder="20000"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Username <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm
                             font-mono focus:outline-none focus:border-brand"
                  placeholder="username-co-UnitedStates"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Password{" "}
                  {proxy.password_masked && (
                    <span className="text-gray-400">
                      (currently {proxy.password_masked} — leave blank to keep)
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    className="w-full border border-gray-300 rounded px-2 py-1.5 pr-14 text-sm
                               font-mono focus:outline-none focus:border-brand"
                    placeholder={proxy.password_masked || "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400
                               hover:text-gray-600"
                  >
                    {showPw ? "hide" : "show"}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  No colons allowed in host, username, or password (proxy format constraint).
                </p>
              </div>
            </>
          )}

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1.5">
              {error}
            </p>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="bg-brand hover:bg-brand-dark text-white rounded px-3 py-1.5 text-sm
                         disabled:opacity-40 transition-colors"
            >
              {save.isPending ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setEditing(false)}
              className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5"
            >
              Cancel
            </button>
            {proxy.source === "custom" && (
              <button
                onClick={() => {
                  setError(null);
                  reset.mutate();
                }}
                disabled={reset.isPending}
                className="ml-auto text-xs text-gray-400 hover:text-red-600 transition-colors"
              >
                {reset.isPending ? "Resetting…" : "Revert to default"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function FcrPage() {
  const [raw, setRaw] = useState("");
  const [enqueueResult, setEnqueueResult] = useState<EnqueueResult | null>(null);
  const [enqueueError, setEnqueueError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<{ text: string; error: boolean } | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [force, setForce] = useState(false);
  const logRef = useRef<HTMLPreElement>(null);
  // Whether the log pane is scrolled to (near) the bottom. Only auto-scroll on
  // new output when the operator hasn't scrolled up to read earlier lines.
  const logPinnedRef = useRef(true);

  function handleLogScroll() {
    const el = logRef.current;
    if (!el) return;
    logPinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  }

  // Parse numbers once per raw change (useMemo — not recalculated on every render)
  const parsed = useMemo(() => parseNumbers(raw), [raw]);

  const {
    data: status,
    refetch,
    isError: statusError,
  } = useQuery({
    queryKey: ["fcr-status"],
    queryFn: fcrStatus,
    // Poll faster while script is running
    refetchInterval: (query) =>
      query.state.data?.running ? 2000 : 5000,
  });

  // Auto-scroll log to bottom, but only when already pinned there.
  useEffect(() => {
    if (logRef.current && logPinnedRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [status?.log_tail]);

  const enqueue = useMutation({
    mutationFn: () => {
      if (!parsed.valid.length) throw new Error("No valid 10-digit NANP numbers found");
      return fcrEnqueue(parsed.valid, force);
    },
    onSuccess: (res) => {
      setEnqueueResult(res);
      setEnqueueError(null);
      setRaw("");
      setForce(false);
      refetch();
    },
    onError: (e) => {
      setEnqueueError(readApiError(e));
      setEnqueueResult(null);
    },
  });

  const trigger = useMutation({
    mutationFn: fcrTrigger,
    onSuccess: (res) => {
      setActionMsg({ text: res.message, error: false });
      refetch();
    },
    onError: (e) => setActionMsg({ text: readApiError(e), error: true }),
  });

  const stop = useMutation({
    mutationFn: fcrStop,
    onSuccess: (res) => {
      setActionMsg({ text: res.message, error: false });
      refetch();
    },
    onError: (e) => setActionMsg({ text: readApiError(e), error: true }),
  });

  const clearQueue = useMutation({
    mutationFn: fcrClearQueue,
    onSuccess: (res) => {
      setConfirmClear(false);
      setActionMsg({ text: res.message, error: false });
      refetch();
    },
    onError: (e) => {
      setConfirmClear(false);
      setActionMsg({ text: readApiError(e), error: true });
    },
  });

  const running = status?.running ?? false;
  const outstanding = status?.outstanding ?? 0;
  const queued = status?.queued ?? 0;
  // Progress reflects THIS queue, not all-time history: how many of the numbers
  // currently in the queue are already registered (queued − outstanding), over
  // the queue size. done.txt's lifetime total would otherwise read ~100% before
  // a fresh run even begins.
  const doneInQueue = Math.max(0, queued - outstanding);
  const progressPct = queued > 0 ? Math.round((doneInQueue / queued) * 100) : 0;

  const anyMutating =
    trigger.isPending || stop.isPending || clearQueue.isPending;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-1">Free Caller Registry</h1>
      <p className="text-sm text-gray-500 mb-6">
        Submit phone numbers for FCR reputation registration. Any format is accepted —
        the system normalizes before enqueueing and deduplicates against numbers already
        registered or already in queue.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Left: enqueue ─────────────────────────────────────────── */}
        <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
          <h2 className="font-semibold text-gray-800">Enqueue numbers</h2>
          <p className="text-xs text-gray-500">
            Paste any format — (619) 555-1234, 1-714-555-9876, 6195551234 x5, mixed
            separators — one per line or comma-separated. International and N11 codes
            are automatically rejected.
          </p>

          <textarea
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono
                       h-56 resize-y focus:outline-none focus:border-brand"
            placeholder={"(619) 555-1234\n+1 714-555-9876\n6195559999 ext. 5\n..."}
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
              setEnqueueResult(null);
              setEnqueueError(null);
            }}
          />

          {/* Live parse feedback */}
          {raw.trim() && (
            <div className="flex flex-wrap gap-3 text-xs">
              {parsed.valid.length > 0 && (
                <span className="text-green-700 font-medium">
                  {fmt(parsed.valid.length)} valid
                </span>
              )}
              {parsed.dupeCount > 0 && (
                <span className="text-gray-400">
                  {fmt(parsed.dupeCount)} duplicate{parsed.dupeCount !== 1 ? "s" : ""} removed
                </span>
              )}
              {parsed.invalidCount > 0 && (
                <span className="text-amber-600">
                  {fmt(parsed.invalidCount)} invalid (non-NANP or too short)
                </span>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              disabled={parsed.valid.length === 0 || enqueue.isPending}
              onClick={() => enqueue.mutate()}
              className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm
                         disabled:opacity-40 transition-colors"
            >
              {enqueue.isPending
                ? "Enqueueing…"
                : `Enqueue ${parsed.valid.length > 0 ? fmt(parsed.valid.length) + " " : ""}numbers`}
            </button>
          </div>

          <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              className="w-3.5 h-3.5 accent-brand"
            />
            Re-register numbers already marked registered
            <span className="text-gray-400">(use if a prior run marked them by mistake)</span>
          </label>

          {enqueueResult && <EnqueueBanner result={enqueueResult} />}
          {enqueueResult && enqueueResult.accepted > 0 && running && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              A run is currently active — it won't pick up these newly added numbers.
              Start another run once it finishes to process them.
            </p>
          )}
          {enqueueError && (
            <p className="text-sm px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
              {enqueueError}
            </p>
          )}
        </div>

        {/* ── Right: status + controls ───────────────────────────────── */}
        <div className="space-y-5">
          {/* Status card */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800">Script status</h2>
              <StatusBadge running={running} />
            </div>

            {statusError && (
              <p className="mb-4 text-sm px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200">
                Couldn't load run status — retrying. This page is admin-only; if
                you're signed in as a non-admin you won't have access.
              </p>
            )}

            {/* Stats — the current queue: still-outstanding vs. already done. */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <StatTile label="Outstanding" value={outstanding} />
              <StatTile label="Registered (this queue)" value={doneInQueue} />
            </div>

            {/* Progress bar (only when there's a queue to show) */}
            {queued > 0 && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{progressPct}% complete</span>
                  <span>{fmt(doneInQueue)} / {fmt(queued)}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="bg-brand rounded-full h-2 transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            )}

            {running && status?.pid && (
              <p className="text-xs text-gray-400 mb-4">PID {status.pid}</p>
            )}

            {/* Primary action button */}
            {running ? (
              <button
                disabled={stop.isPending}
                onClick={() => {
                  setActionMsg(null);
                  stop.mutate();
                }}
                className="w-full bg-red-600 hover:bg-red-700 text-white rounded px-4 py-2
                           text-sm disabled:opacity-40 transition-colors"
              >
                {stop.isPending ? "Stopping…" : "Stop script"}
              </button>
            ) : (
              <button
                disabled={outstanding === 0 || anyMutating}
                onClick={() => {
                  setActionMsg(null);
                  trigger.mutate();
                }}
                className="w-full bg-brand hover:bg-brand-dark text-white rounded px-4 py-2
                           text-sm disabled:opacity-40 transition-colors"
              >
                {trigger.isPending ? "Starting…" : "Start registration run"}
              </button>
            )}

            {/* Clear queue link — when idle and pending.txt has anything to clear */}
            {!running && queued > 0 && (
              <div className="mt-3 text-center">
                {confirmClear ? (
                  <span className="text-xs text-gray-600">
                    Clear {fmt(queued)} queued numbers?{" "}
                    <button
                      onClick={() => clearQueue.mutate()}
                      disabled={clearQueue.isPending}
                      className="text-red-600 hover:underline font-medium"
                    >
                      {clearQueue.isPending ? "Clearing…" : "Yes, clear"}
                    </button>{" "}
                    ·{" "}
                    <button
                      onClick={() => setConfirmClear(false)}
                      className="text-gray-500 hover:underline"
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    onClick={() => setConfirmClear(true)}
                    className="text-xs text-gray-400 hover:text-red-600 transition-colors"
                  >
                    Clear queue
                  </button>
                )}
              </div>
            )}

            {actionMsg && (
              <p
                className={`mt-3 text-sm px-3 py-2 rounded ${
                  actionMsg.error
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-blue-50 text-blue-700 border border-blue-200"
                }`}
              >
                {actionMsg.text}
              </p>
            )}
          </div>

          {/* Proxy config */}
          <ProxyCard />

          {/* Log tail */}
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-800">Log</h2>
              <span className="text-xs text-gray-400">
                Last 80 lines · {running ? "2s" : "5s"} refresh
              </span>
            </div>
            <pre
              ref={logRef}
              onScroll={handleLogScroll}
              className="bg-gray-950 text-gray-100 rounded p-3 text-xs font-mono
                         overflow-y-auto h-64 whitespace-pre-wrap leading-relaxed"
            >
              {status?.log_tail?.length
                ? status.log_tail.join("\n")
                : "No log output yet. Start a run to see progress here."}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}
